import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControlLabel from '@mui/material/FormControlLabel'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import {
  USER_ROLES,
  USER_ROLE_LABELS,
  userInputSchema,
  type UserDto,
  type UserInput,
} from '@lawyer/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import type { z } from 'zod'
import { useAuth } from '../../auth/AuthProvider'
import { ErrorAlert, LoadingBlock } from '../../components/common/Feedback'
import { PageHeader } from '../../components/common/PageHeader'
import { EnumSelect } from '../../components/common/fields'
import { api } from '../../lib/api'
import { formResolver } from '../../lib/form'
import { qk } from '../../lib/queryClient'

type FormValues = z.input<typeof userInputSchema>

const EMPTY: FormValues = { email: '', name: '', role: 'USER', isActive: true }

function UserDialog({
  open,
  user,
  onClose,
}: {
  open: boolean
  user: UserDto | null
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const form = useForm<FormValues, unknown, UserInput>({
    resolver: formResolver(userInputSchema),
    defaultValues: EMPTY,
  })

  useEffect(() => {
    if (!open) return
    form.reset(
      user ? { email: user.email, name: user.name, role: user.role, isActive: user.isActive } : EMPTY
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user?.id])

  const save = useMutation({
    mutationFn: (values: UserInput) =>
      user ? api.patch<UserDto>(`/users/${user.id}`, values) : api.post<UserDto>('/users', values),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['users'] })
      onClose()
    },
  })

  const { errors } = form.formState

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <form onSubmit={form.handleSubmit((values) => save.mutate(values))} noValidate>
        <DialogTitle>{user ? 'עריכת משתמש' : 'משתמש חדש'}</DialogTitle>
        <DialogContent dividers>
          {save.error ? <ErrorAlert error={save.error} /> : null}
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <TextField
              label="שם"
              required
              autoFocus
              {...form.register('name')}
              error={Boolean(errors.name)}
              helperText={errors.name?.message}
            />
            <TextField
              label="חשבון Google (דואר אלקטרוני)"
              type="email"
              required
              {...form.register('email')}
              error={Boolean(errors.email)}
              helperText={errors.email?.message ?? 'ההתחברות מתבצעת עם חשבון זה'}
            />
            <EnumSelect
              control={form.control}
              name="role"
              label="רמת הרשאה"
              options={USER_ROLES.map((role) => ({ id: role, label: USER_ROLE_LABELS[role] }))}
            />
            <Controller
              control={form.control}
              name="isActive"
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(field.value)}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  }
                  label="חשבון פעיל"
                />
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>ביטול</Button>
          <Button type="submit" variant="contained" disabled={save.isPending}>
            שמירה
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export function UsersPage() {
  const queryClient = useQueryClient()
  const { user: currentUser } = useAuth()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<UserDto | null>(null)

  const { data, isPending, error, refetch } = useQuery({
    queryKey: qk.users({ includeInactive: true }),
    queryFn: () => api.get<UserDto[]>('/users', { includeInactive: true }),
  })

  const setActive = useMutation({
    mutationFn: (vars: { id: string; isActive: boolean }) =>
      api.post(`/users/${vars.id}/active`, { isActive: vars.isActive }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['users'] }),
  })

  return (
    <>
      <PageHeader
        title="ניהול משתמשים"
        subtitle="הוספת עובדים וקביעת רמת הרשאה. ההתחברות מתבצעת עם חשבון ה־Google של העובד."
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditing(null)
              setDialogOpen(true)
            }}
          >
            משתמש חדש
          </Button>
        }
      />

      {error ? <ErrorAlert error={error} onRetry={() => void refetch()} /> : null}
      {setActive.error ? <ErrorAlert error={setActive.error} /> : null}
      {isPending ? <LoadingBlock /> : null}

      {data ? (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>שם</TableCell>
                <TableCell>דואר אלקטרוני</TableCell>
                <TableCell>הרשאה</TableCell>
                <TableCell>מצב</TableCell>
                <TableCell align="left" />
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((user) => (
                <TableRow key={user.id} hover>
                  <TableCell>
                    {user.name}
                    {user.id === currentUser?.id ? (
                      <Typography component="span" variant="caption" color="text.secondary">
                        {' '}
                        (אני)
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={USER_ROLE_LABELS[user.role]}
                      color={user.role === 'ADMIN' ? 'primary' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Switch
                      size="small"
                      checked={user.isActive}
                      disabled={user.id === currentUser?.id || setActive.isPending}
                      onChange={(e) => setActive.mutate({ id: user.id, isActive: e.target.checked })}
                    />
                  </TableCell>
                  <TableCell align="left">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setEditing(user)
                        setDialogOpen(true)
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', p: 2 }}>
            לא ניתן להשבית את החשבון שאיתו אתה מחובר או את האדמין האחרון במערכת.
          </Typography>
        </Paper>
      ) : null}

      <UserDialog open={dialogOpen} user={editing} onClose={() => setDialogOpen(false)} />
    </>
  )
}
